import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import { Box, Button, Divider, Typography } from "@mui/material";

import APP_ROUTES from "../../../../../routers/routeConstants";

import { CreateServiceFormData } from "../../../types/service.type";
import { createServiceSchema } from "../../../schemas/service.schema";
import { ArrowBackButtonIcon } from "../../../assets/icons/go-hire-icons";
import { CurrencyEnum, ServiceRequestTypeEnum, ServiceTypeEnum } from "../../../enum/service.enum";

import { ServiceInformation, CustomerPreferences, PricingAndTerms, AvailabilityAndDelivery } from "./Steps";
import { useCreateService } from "../../../api/mutations/useCreateService";
import { useMinimumServicePrices } from "../../../../../api/queries/payment/paymentQueries";

export type CreateServiceStep =
  | "serviceInformation"
  | "customerPreferences"
  | "pricingAndTerms"
  | "availabilityAndDelivery";

const createServiceSteps: CreateServiceStep[] = [
  "serviceInformation",
  "availabilityAndDelivery",
  "customerPreferences",
  "pricingAndTerms",
];

const CreateService = () => {
  const { t, i18n } = useTranslation("goService", { keyPrefix: "createServicePage" });
  const [currentStep, setCurrentStep] = useState<CreateServiceStep>("serviceInformation");
  const navigate = useNavigate();

  const methods = useForm<CreateServiceFormData>({
    resolver: zodResolver(createServiceSchema),
    mode: "all",
    defaultValues: {
      type: ServiceTypeEnum.OneTime,
      availability: {
        days: [],
        months: [],
        locations: [],
        alwaysAvailable: false,
        availableAllDay: false,
        from: undefined,
        to: undefined,
        option: "online",
      },
      customerPreferenceConfig: {
        restrict: true,
        ageRange: {
          minAge: 15,
          maxAge: 40,
        },
        personas: [],
        locations: [],
      },
      paymentConfig: {
        currency: CurrencyEnum.EGP,
        type: undefined,
        amount: undefined,
        unit: undefined,
        notExpired: false,
        expiryDate: undefined,
      },
    },
  });

  let stepComponent = null;

  if (currentStep === "serviceInformation") stepComponent = <ServiceInformation />;
  if (currentStep === "customerPreferences") stepComponent = <CustomerPreferences />;
  if (currentStep === "pricingAndTerms") stepComponent = <PricingAndTerms />;
  if (currentStep === "availabilityAndDelivery") stepComponent = <AvailabilityAndDelivery />;

  const handleCancel = () => navigate(APP_ROUTES.CREATE_SERVICE);

  const handlePrevious = () => {
    const currentStepIndex = createServiceSteps.indexOf(currentStep);
    if (currentStepIndex > 0) {
      setCurrentStep(createServiceSteps[currentStepIndex - 1]);
    }
  };

  const handleBackButton = () => {
    if (currentStep === createServiceSteps[0]) {
      handleCancel();
    } else {
      handlePrevious();
    }
  };

  const { mutate: createService } = useCreateService((data: any) => {
    navigate(APP_ROUTES.CREATE_SERVICE, {
      replace: true,
      state: { serviceCreated: true, serviceId: data.id, serviceType: data.type },
    });
  });

  const { minimumServicePrices } = useMinimumServicePrices();

  const handleNext = async () => {
    let isValid = false;

    if (currentStep === createServiceSteps[0]) {
      isValid = await methods.trigger(["type", "name", "description", "category", "subCategory", "banner"]);
      if (!isValid) return;
      setCurrentStep(createServiceSteps[1]);
    } else if (currentStep === createServiceSteps[1]) {
      isValid = await methods.trigger(["availability.option"]);
      if (!isValid) return;

      const isAlwaysAvailable = methods.watch("availability.alwaysAvailable");
      const isAvailableAllDay = methods.watch("availability.availableAllDay");
      const isOptionOnline = methods.watch("availability.option") === "online";

      if (!isOptionOnline) {
        isValid = await methods.trigger(["availability.locations"]);
        if (!isValid) return;
      }
      if (!isAlwaysAvailable) {
        isValid = await methods.trigger(["availability.days", "availability.months"]);
        if (!isValid) return;
      }

      if (!isAvailableAllDay) {
        isValid = await methods.trigger(["availability.from", "availability.to"]);
        if (!isValid) return;
      }

      setCurrentStep(createServiceSteps[2]);
    } else if (currentStep === createServiceSteps[2]) {
      const isRestricted = methods.watch("customerPreferenceConfig.restrict");

      if (isRestricted) {
        isValid = await methods.trigger([
          "customerPreferenceConfig.ageRange",
          "customerPreferenceConfig.personas",
          "customerPreferenceConfig.restrict",
          "customerPreferenceConfig.locations",
        ]);
      } else {
        isValid = await methods.trigger(["customerPreferenceConfig.ageRange"]);
      }
      if (!isValid) return;
      setCurrentStep(createServiceSteps[3]);
    } else if (currentStep === createServiceSteps[3]) {
      isValid = await methods.trigger([
        "paymentConfig.type",
        "paymentConfig.allowGoalngoMemebers",
        "paymentConfig.agreeToTerms",
      ]);
      if (!isValid) return;

      const isServiceHasNoExpired = methods.watch("paymentConfig.notExpired");
      const servicePaymentConfigType = methods.watch("paymentConfig.type");

      if (servicePaymentConfigType === ServiceRequestTypeEnum.Fixed) {
        isValid = await methods.trigger(["paymentConfig.amount", "paymentConfig.currency"]);
        const amount = methods.getValues("paymentConfig.amount");
        const currency = methods.getValues("paymentConfig.currency");
        const minimumServicePrice = minimumServicePrices?.[currency as keyof typeof minimumServicePrices];
        if (minimumServicePrice && amount < minimumServicePrice) {
          isValid = false;
          methods.setError("paymentConfig.amount", {
            message: "AMOUNT_MUST_BE_GREATER_THAN_MINIMUM_SERVICE_PRICE",
          });
          return;
        }
      }
      if (servicePaymentConfigType === ServiceRequestTypeEnum.Unit) {
        isValid = await methods.trigger(["paymentConfig.unit", "paymentConfig.amount", "paymentConfig.currency"]);
        if (!isValid) return;
        const amount = methods.getValues("paymentConfig.amount");
        const currency = methods.getValues("paymentConfig.currency");
        const minimumServicePrice = minimumServicePrices?.[currency as keyof typeof minimumServicePrices];
        if (minimumServicePrice && amount < minimumServicePrice) {
          isValid = false;
          methods.setError("paymentConfig.amount", {
            message: "AMOUNT_MUST_BE_GREATER_THAN_MINIMUM_SERVICE_PRICE",
          });
          return;
        }
      }

      if (!isServiceHasNoExpired) {
        isValid = await methods.trigger(["paymentConfig.expiryDate"]);
        if (!isValid) return;
      }

      const formData = methods.getValues();
      createService(formData);
    }
  };

  return (
    <FormProvider {...methods}>
      <Box sx={styles.container}>
        <Button
          startIcon={<ArrowBackButtonIcon />}
          variant="text-md"
          onClick={handleCancel}
          sx={styles.backButton(i18n.language === "ar")}
        >
          {t("createService")}
        </Button>
        <Box sx={styles.contentContainer}>
          <Box sx={styles.titleContainer}>
            <Typography sx={styles.title}>{t(currentStep)}</Typography>
            <Box sx={styles.progressContainer}>
              {createServiceSteps.map((step, index) => (
                <Box
                  key={index}
                  sx={styles.progressLine(
                    currentStep === createServiceSteps[3]
                      ? index <= 3
                      : currentStep === createServiceSteps[2]
                      ? index <= 2
                      : currentStep === createServiceSteps[1]
                      ? index <= 1
                      : index === 0
                  )}
                />
              ))}
            </Box>
          </Box>
          <Divider sx={styles.divider} />
          {stepComponent}
          <Divider sx={styles.divider} />
          <Box sx={styles.footerContainer}>
            <Button variant="outlined-md" onClick={handleBackButton}>
              {currentStep === createServiceSteps[0] ? t("cancel") : t("previous")}
            </Button>
            <Button
              variant="primary-md"
              onClick={handleNext}
              type={currentStep === createServiceSteps[3] ? "submit" : "button"}
            >
              {currentStep === createServiceSteps[3] ? t("publish") : t("next")}
            </Button>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default CreateService;

const styles = {
  container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  contentContainer: {
    backgroundColor: "var(--Base-White, #FFF)",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "20px",
    border: "1px solid #E4E7EC",
  },
  divider: {
    mx: "-20px",
  },
  footerContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
  },
  backButton: (isArabic: boolean) => ({
    width: "fit-content",
    background: "transparent !important",
    color: "var(--black-100, #1C1C1C) !important",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "30px",
    gap: "8px",
    "& .MuiButton-startIcon": {
      transform: isArabic ? "scaleX(-1)" : "scaleX(1)",
    },
  }),
  title: {
    fontFamily: "Montserrat",
    color: "var(--black-100, #1C1C1C)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  },
  progressContainer: {
    display: "flex",
    gap: "4px",
    width: "100%",
  },
  progressLine: (isCompleted: boolean) => ({
    flex: 1,
    height: "4px",
    backgroundColor: isCompleted ? "var(--Brand-600, #008B07)" : "var(--Gray-200, #E4E7EC)",
    borderRadius: "2px",
  }),
};
